import { Geofencing } from 'civic-champs-shared/core/location/utils'
import { DEFAULT_GEOFENCING } from 'civic-champs-shared/constants/GEO_DATA'
import { LocationAddressType } from '../types'
import { extractPlusCode } from '../../civic-champs-shared/core/location/plus-code-utils'

export { extractPlusCode }

export interface GeocodedAddressFormValue {
  line1: string
  line2?: string
  city: string
  state: string
  zip: string
  country?: string
  countryCode?: string
  county?: string
  geofencing: Geofencing
}

export const EMPTY_GEOCODED_FIELD: GeocodedAddressFormValue = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  countryCode: '',
  county: '',
  geofencing: DEFAULT_GEOFENCING,
}

export const hasGeocodedLocation = (type?: LocationAddressType): boolean =>
  type === LocationAddressType.AddressAndCoords || type === LocationAddressType.PlusCode

export const resolveLocationDisplayName = ({
  name,
  locationDetails,
  addressType,
}: {
  name?: string | null
  locationDetails?: string | null
  addressType?: LocationAddressType
}): string => {
  if (addressType === LocationAddressType.PlusCode) {
    return locationDetails?.trim() || name?.trim() || ''
  }

  return name?.trim() || ''
}

export const deriveAddressTypeFromLocation = (location?: {
  locationAddressType?: LocationAddressType
  locationIsAddress?: boolean
  locationDetails?: string | null
}): LocationAddressType => {
  if (location?.locationAddressType) {
    return location.locationAddressType
  }
  if (location?.locationIsAddress === false) {
    return LocationAddressType.CustomTextOnly
  }
  if (location?.locationDetails && location?.locationIsAddress) {
    return LocationAddressType.PlusCode
  }
  return LocationAddressType.AddressAndCoords
}

export const buildGeocodedFieldFromLocation = (
  location?: {
    line1?: string | null
    line2?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
    countryCode?: string | null
    county?: string | null
    geofencing?: { location?: unknown } | null
  },
  mapGeofencing?: (geofencing: { location?: unknown }) => Geofencing,
): GeocodedAddressFormValue => {
  const geofencing =
    location?.geofencing?.location && mapGeofencing ? mapGeofencing(location.geofencing) : DEFAULT_GEOFENCING

  return {
    line1: location?.line1 || '',
    line2: location?.line2 || '',
    city: location?.city || '',
    state: location?.state || '',
    zip: location?.zipCode || '',
    country: location?.county || '',
    countryCode: location?.countryCode || '',
    county: location?.county || '',
    geofencing,
  }
}

export const mapLocationAddressFormToPayload = ({
  addressType,
  customAddressType,
  address,
  plusCode,
  locationDetails,
}: {
  addressType?: LocationAddressType
  customAddressType?: LocationAddressType
  address?: GeocodedAddressFormValue
  plusCode?: GeocodedAddressFormValue
  locationDetails?: string | null
}) => {
  const resolvedType = addressType ?? customAddressType ?? LocationAddressType.AddressAndCoords
  const geocoded = hasGeocodedLocation(resolvedType)
  const geocodedSource =
    resolvedType === LocationAddressType.PlusCode && plusCode
      ? { ...plusCode, line1: extractPlusCode(plusCode.line1) }
      : address

  return {
    ...(geocoded ? geocodedSource : {}),
    // The API persists the postal code and country under different names than
    // the geocoded form value uses, and drops anything it does not recognise.
    ...(geocoded ? { zipCode: geocodedSource?.zip || null, countryCode: geocodedSource?.countryCode || null } : {}),
    locationDetails:
      resolvedType === LocationAddressType.CustomTextOnly || resolvedType === LocationAddressType.PlusCode
        ? locationDetails || null
        : null,
    locationIsAddress: geocoded,
    locationAddressType: resolvedType,
  }
}

export const deriveAddressTypeFromTemplate = (template?: {
  locationAddressType?: LocationAddressType
  locationIsAddress?: boolean
  locationDetails?: string | null
}): LocationAddressType => deriveAddressTypeFromLocation(template)

export const mapTemplateCustomLocationToApiPayload = ({
  useCustomAddress,
  organizationLocationId,
  streetAddress,
  city,
  state,
  zip,
  plusCode,
  customAddressType,
  locationDetails,
  geofencing,
}: {
  useCustomAddress: boolean
  organizationLocationId: number | null
  streetAddress: string
  city: string
  state: string
  zip: string
  plusCode?: string
  customAddressType?: LocationAddressType
  locationDetails: string
  geofencing: Geofencing
}) => {
  if (!useCustomAddress || organizationLocationId) {
    return {
      organizationLocationId: organizationLocationId || null,
      streetAddress: null,
      city: null,
      state: null,
      zip: null,
      locationDetails: null,
      locationAddressType: LocationAddressType.AddressAndCoords,
      geofencing: null,
    }
  }

  const resolvedType = customAddressType ?? LocationAddressType.AddressAndCoords
  const mapped = mapLocationAddressFormToPayload({
    customAddressType: resolvedType,
    address: {
      line1: streetAddress,
      city,
      state,
      zip,
      geofencing,
    },
    plusCode: {
      line1: extractPlusCode(plusCode || ''),
      city: '',
      state: '',
      zip: '',
      geofencing,
    },
    locationDetails,
  })
  const mappedLine1 = (mapped as { line1?: string | null }).line1

  return {
    organizationLocationId: null,
    streetAddress: mapped.locationIsAddress ? mappedLine1 ?? null : null,
    city: resolvedType === LocationAddressType.AddressAndCoords ? city : null,
    state: resolvedType === LocationAddressType.AddressAndCoords ? state : null,
    zip: resolvedType === LocationAddressType.AddressAndCoords ? zip : null,
    locationDetails: mapped.locationDetails,
    locationAddressType: mapped.locationAddressType,
    geofencing: mapped.locationIsAddress ? geofencing : null,
  }
}
